import React, { useEffect, useState } from 'react';
import { ScrollView, StyleSheet, TouchableOpacity, View, useWindowDimensions } from 'react-native';
import Popover from 'react-native-popover-view';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { ZenIcon, ZenText } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { claimOfferForCart } from '../../../redux/slices/newSellscreenSlice';
import { ClaimableOffer, percentOf } from '../../../redux/utils/discountEngine';
import { describeBuy, formatMinor } from './discountLabels';

// Shows a Buy X Get Y offer's details; for a category "get" the cashier picks the product
const ClaimOfferSheet = ({ visible, onClose, offer }: { visible: boolean, onClose: () => void, offer: ClaimableOffer }) => {
	const dispatch = useAppDispatch()
	const { width, height } = useWindowDimensions()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null)
	const [loading, setLoading] = useState(false)

	useEffect(() => {
		if (visible) setSelectedVariantId(offer.options.length === 1 ? offer.options[0].variantId : null)
	}, [visible, offer])

	const selected = offer.options.find((option) => option.variantId === selectedVariantId)

	const claim = () => {
		if (!selected) return
		setLoading(true)
		dispatch(claimOfferForCart({ locationId, discountId: offer.discountId, getVariantId: selected.variantId, getProductId: selected.productId }))
			.unwrap()
			.then(onClose)
			.catch(() => undefined) // the slice shows the reason
			.finally(() => setLoading(false))
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<Popover isVisible={visible} onRequestClose={onClose} popoverStyle={{ width: width > 900 ? 500 : width > 600 ? 450 : 340, borderRadius: 12 }}>
						<View style={[styles.container, { maxHeight: height * 0.75 }]}>
							<View style={styles.header}>
								<ZenText content={offer.title} style={styles.title} />
								<TouchableOpacity onPress={onClose}>
									<ZenIcon name='close' type='antdesign' size={18} color={theme?.disabledGray500} />
								</TouchableOpacity>
							</View>

							<View style={styles.detailRow}>
								<ZenText content="BUY" style={styles.detailLabel} />
								<ZenText content={describeBuy(offer.buy)} style={styles.detailValue} />
							</View>
							<View style={styles.detailRow}>
								<ZenText content="GET" style={styles.detailLabel} />
								<ZenText content={`${offer.getQuantity} × ${offer.getType === "CATEGORY" ? `any ${offer.getName} item` : offer.getName} at ${offer.percent >= 100 ? "no cost" : `${offer.percent}% off`}`} style={styles.detailValue} />
							</View>

							<ZenText content={offer.getType === "CATEGORY" ? "Choose the item" : "Offer item"} style={styles.sectionLabel} />
							<ScrollView style={{ flexGrow: 0 }}>
								{offer.options.map((option) => {
									const isSelected = option.variantId === selectedVariantId
									const saving = percentOf(option.price * offer.getQuantity, offer.percent)
									return (
										<TouchableOpacity key={option.variantId} style={[styles.option, isSelected && styles.optionSelected]} onPress={() => setSelectedVariantId(option.variantId)}>
											<ZenIcon type='material-community' name={isSelected ? 'radiobox-marked' : 'radiobox-blank'} size={18} color={isSelected ? theme?.primary : theme?.grey} />
											<View style={{ flex: 1 }}>
												<ZenText content={option.name} style={styles.optionTitle} />
												<ZenText content={`${formatMinor(currencySymbol, option.price)} each`} style={styles.optionSub} />
											</View>
											<ZenText content={`Save ${formatMinor(currencySymbol, saving)}`} style={styles.optionAmount} />
										</TouchableOpacity>
									)
								})}
							</ScrollView>
							<ZenText content="The item is added to the cart if it is not there yet. Each unit counts toward one offer only." style={styles.hint} />

							<TouchableOpacity style={[styles.claimBtn, (!selected || loading) && { opacity: 0.5 }]} disabled={!selected || loading} onPress={claim}>
								<ZenText content={loading ? "Claiming..." : "Claim offer"} style={styles.claimText} />
							</TouchableOpacity>
						</View>
					</Popover>
				)
			}}
		</ThemeConsumer>
	)
}

const style = (theme: Theme) => StyleSheet.create({
	container: { padding: 16, gap: 10 },
	header: { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' },
	title: { fontSize: theme?.smallFontSize, fontFamily: theme?.bold, color: theme?.lightDark, flex: 1 },
	detailRow: { flexDirection: 'row', gap: 10, alignItems: 'flex-start' },
	detailLabel: { width: 34, fontSize: theme?.miniLabel, fontFamily: theme?.bold, color: theme?.grey, paddingTop: 2 },
	detailValue: { flex: 1, fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.lightDark },
	sectionLabel: { fontSize: theme?.miniLabel, fontFamily: theme?.bold, color: theme?.grey, marginTop: 4, textTransform: 'uppercase' },
	option: { flexDirection: 'row', alignItems: 'center', gap: 10, padding: 12, borderRadius: 10, borderWidth: 1, borderColor: theme?.coolGray, marginBottom: 8 },
	optionSelected: { borderColor: theme?.primary, backgroundColor: theme?.lightPrimary },
	optionTitle: { fontSize: theme?.errorFontSize, fontFamily: theme?.medium, color: theme?.lightDark },
	optionSub: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.grey },
	optionAmount: { fontSize: theme?.errorFontSize, fontFamily: theme?.bold, color: theme?.tealGreen },
	hint: { fontSize: theme?.badgeFontSize, fontFamily: theme?.medium, color: theme?.grey },
	claimBtn: { backgroundColor: theme?.primary, borderRadius: 10, paddingVertical: 12, alignItems: 'center' },
	claimText: { color: theme?.light, fontSize: theme?.errorFontSize, fontFamily: theme?.bold },
})

export default ClaimOfferSheet
